export function isEditableElement(el: HTMLElement | null): boolean {
  if (!el) {
    return false;
  }
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node = el ? el.parentElement : null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function getFocusedScroller(focused: HTMLElement | null): HTMLElement {
  return (getScrollableAncestor(focused) || document.scrollingElement || document.documentElement) as HTMLElement;
}

// tracks addEventListener calls so they can all be removed in one stop()/clear().
export class ListenerGroup {

  private cleanups: Array<() => void> = [];

  add(target: EventTarget, type: string, handler: EventListener, options?: AddEventListenerOptions): void {
    target.addEventListener(type, handler, options);
    this.cleanups.push(() => target.removeEventListener(type, handler, options as any));
  }

  clear(): void {
    this.cleanups.forEach(cleanup => cleanup());
    this.cleanups = [];
  }

  get active(): boolean {
    return this.cleanups.length > 0;
  }
}

// keyboard/viewport signal set- visualViewport pan/resizea and focus changes. handler runs on each.
export function onKeyboardViewportChange(group: ListenerGroup, handler: (event: Event) => void): void {
  if (window.visualViewport) {
    group.add(window.visualViewport, 'resize', handler);
    group.add(window.visualViewport, 'scroll', handler);
  }
  group.add(document, 'focusin', handler, {capture: true});
  group.add(document, 'focusout', handler, {capture: true});
}

export const REVEAL_MARGIN_PX = 12;

// scroll the focused editable back into the visible band of its own scroller (visible bottom =
// min(scroller bottom, vv.height); the scroller can extend behind the keyboard).
export function revealFocusedInScroller(vv: VisualViewport, marginPx: number = REVEAL_MARGIN_PX): void {
  const active = document.activeElement as HTMLElement | null;
  if (!active || !isEditableElement(active)) {
    return;
  }
  const scroller = getFocusedScroller(active);
  if (scroller === document.scrollingElement || scroller === document.documentElement || scroller === document.body) {
    return;
  }
  const el = active.getBoundingClientRect();
  const sc = scroller.getBoundingClientRect();
  const visibleBottom = Math.min(sc.bottom, vv.height);
  if (el.bottom > visibleBottom - marginPx) {
    scroller.scrollTop += el.bottom - visibleBottom + marginPx;
  } else if (el.top < sc.top + marginPx) {
    scroller.scrollTop -= sc.top - el.top + marginPx;
  }
}
